import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Stack,
  Snackbar,
  Tooltip,
  LinearProgress,
  TextField,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Grow,
  alpha,
  Avatar,
  Badge,
  Tabs,
  Tab,
  Grid,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import {
  PictureAsPdf,
  GridOn,
  Print,
  Link as LinkIcon,
  CalendarToday,
  PeopleAlt,
  Download,
  ContentCopy,
  FilterAlt,
  Search,
  Save,
  Event,
  Schedule,
  LocationOn,
  CheckCircle,
  Pending,
  MoreVert,
  FileDownload,
  QrCode,
  Edit,
  Visibility,
  Check,
  Clear,
  Refresh,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { id } from "date-fns/locale";

export default function AttendanceReport() {
  const { meetingId } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [attendances, setAttendances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [minutes, setMinutes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const itemsPerPage = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const loadData = async () => {
      try {
        const meetingRef = doc(db, "meetings", meetingId);
        const meetingSnap = await getDoc(meetingRef);

        if (!meetingSnap.exists()) {
          setError("Rapat tidak ditemukan");
          setLoading(false);
          return;
        }

        const meetingData = { id: meetingSnap.id, ...meetingSnap.data() };
        setMeeting(meetingData);
        setMinutes(meetingData.notes || "");

        const link = `${window.location.origin}/register/${meetingId}`;
        setMeetingLink(link);

        const attendancesRef = collection(
          db,
          `meetings/${meetingId}/attendances`,
        );
        const attendancesSnap = await getDocs(attendancesRef);

        const attendanceList = [];
        attendancesSnap.forEach((doc) => {
          attendanceList.push({ id: doc.id, ...doc.data() });
        });

        setAttendances(attendanceList);
        setLoading(false);
      } catch (err) {
        setError("Gagal memuat data");
        setLoading(false);
        console.error(err);
      }
    };

    loadData();
  }, [meetingId]);

  const handleSaveMinutes = async () => {
    if (!meetingId) return;

    setIsSaving(true);
    try {
      const meetingRef = doc(db, "meetings", meetingId);
      await updateDoc(meetingRef, {
        notes: minutes,
      });

      setSnackbarMessage("Notulen berhasil disimpan!");
      setOpenSnackbar(true);
      setMeeting((prev) => ({ ...prev, notes: minutes }));
      setIsEditingNotes(false);
    } catch (err) {
      setError("Gagal menyimpan notulen");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAttendances = attendances.filter(
    (att) =>
      att.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      att.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (att.email && att.email.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  // Pagination
  const totalPages = Math.ceil(filteredAttendances.length / itemsPerPage);
  const paginatedAttendances = filteredAttendances.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Page 1: Attendance List
      const logo = new Image();
      logo.src = "/logo/logo.png";

      logo.onload = () => {
        // Header
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("LAPORAN KEHADIRAN RAPAT", 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Rapat: ${meeting?.name || ""}`, 14, 35);
        doc.text(
          `Tanggal: ${meeting?.day || ""}, ${meeting?.date || ""}`,
          14,
          40,
        );
        doc.text(
          `Waktu: ${meeting?.startTime || ""} - ${meeting?.endTime || ""}`,
          14,
          45,
        );
        doc.text(`Lokasi: ${meeting?.location || "-"}`, 14, 50);

        doc.line(14, 55, 200, 55);

        // Table
        const tableData = paginatedAttendances.map((att, index) => [
          index + 1 + (page - 1) * itemsPerPage,
          att.name,
          att.position,
          att.notelp || "-",
          format(new Date(att.attendedAt), "PPpp", { locale: id }),
          att.signature ? "Ada" : "-",
        ]);

        autoTable(doc, {
          head: [
            ["No", "Nama", "Posisi", "No Telp", "Waktu Hadir", "Tanda Tangan"],
          ],
          body: tableData,
          startY: 60,
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [46, 125, 50],
            textColor: 255,
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
        });

        // Page 2: Meeting Minutes
        doc.addPage();

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("NOTULEN RAPAT", 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Rapat: ${meeting?.name || ""}`, 14, 35);
        doc.text(
          `Tanggal: ${meeting?.day || ""}, ${meeting?.date || ""}`,
          14,
          40,
        );

        doc.line(14, 45, 200, 45);

        doc.setFontSize(10);
        const splitMinutes = doc.splitTextToSize(
          meeting?.notes || "Tidak ada notulen",
          180,
        );
        doc.text(splitMinutes, 14, 55);

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.text(`Halaman ${i} dari ${pageCount}`, 105, 285, {
            align: "center",
          });
        }

        doc.save(
          `Laporan_Kehadiran_${meeting?.name || "Rapat"}_${meeting?.date || ""}.pdf`,
        );
      };
    } catch (error) {
      console.error("Error saat membuat PDF:", error);
      setSnackbarMessage("Gagal membuat PDF. Silakan coba lagi.");
      setOpenSnackbar(true);
    }
  };

  const handleExportCSV = () => {
    try {
      let csvContent = "data:text/csv;charset=utf-8,";
      csvContent += "No,Nama,Jabatan,No Telp,Waktu Hadir,Tanda Tangan\n";

      filteredAttendances.forEach((att, index) => {
        const row = [
          index + 1,
          `"${att.name}"`,
          `"${att.position}"`,
          `"${att.notelp || ""}"`,
          `"${format(new Date(att.attendedAt), "PPpp", { locale: id })}"`,
          `"${att.signature ? "Ada" : "Tidak ada"}"`,
        ].join(",");
        csvContent += row + "\n";
      });

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `daftar-hadir-${meetingId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbarMessage("CSV berhasil diekspor!");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error saat export CSV:", error);
      setSnackbarMessage("Gagal mengekspor CSV");
      setOpenSnackbar(true);
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById("qr-code");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngUrl = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-code-rapat-${meetingId}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setSnackbarMessage("QR Code berhasil diunduh!");
      setOpenSnackbar(true);
    };

    img.src =
      "data:image/svg+xml;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(meetingLink);
    setSnackbarMessage("Tautan berhasil disalin!");
    setOpenSnackbar(true);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <Fade in>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              textAlign: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Memuat data kehadiran...
            </Typography>
          </Paper>
        </Box>
      </Fade>
    );
  }

  if (error) {
    return (
      <Fade in>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
              "& .MuiAlert-icon": { fontSize: 24 },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => window.location.reload()}
              >
                Coba Lagi
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 3,
            borderRadius: 4,
            background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)`,
            color: "white",
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          }}
        >
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 0}
          >
            <Stack spacing={1}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                fontWeight={800}
                sx={{ letterSpacing: -0.5 }}
              >
                Rekap Kehadiran Rapat
              </Typography>
              <Typography variant={isMobile ? "body1" : "h6"} fontWeight={500}>
                {meeting?.name}
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  icon={<CalendarToday sx={{ fontSize: 16 }} />}
                  label={`${meeting?.day || ""}, ${meeting?.date || ""}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                />
                <Chip
                  icon={<Schedule sx={{ fontSize: 16 }} />}
                  label={`${meeting?.startTime || ""} - ${meeting?.endTime || ""}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                />
                <Chip
                  icon={<PeopleAlt sx={{ fontSize: 16 }} />}
                  label={`${attendances.length} Peserta`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                />
              </Stack>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ width: isMobile ? "100%" : "auto" }}
            >
              <Tooltip title="Export PDF" arrow>
                <IconButton
                  onClick={handleExportPDF}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.25),
                    },
                  }}
                >
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Excel" arrow>
                <IconButton
                  onClick={handleExportCSV}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.25),
                    },
                  }}
                >
                  <GridOn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cetak" arrow>
                <IconButton
                  onClick={() => window.print()}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.25),
                    },
                  }}
                >
                  <Print />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh" arrow>
                <IconButton
                  onClick={() => window.location.reload()}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                    color: "white",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.25),
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: isMobile ? "0.8rem" : "0.9rem",
                minHeight: 48,
              },
              "& .Mui-selected": {
                color: "#2e7d32",
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#2e7d32",
                height: 3,
              },
            }}
          >
            <Tab label="📋 Kehadiran" />
            <Tab label="📝 Notulen" />
            <Tab label="📱 QR Code" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ display: tabValue === 0 ? "block" : "none" }}>
          {/* Attendance List */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "stretch" : "center"}
              spacing={isMobile ? 2 : 0}
              sx={{ mb: 3 }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <PeopleAlt sx={{ color: "#2e7d32" }} />
                Daftar Hadir
                <Chip
                  label={`${filteredAttendances.length} peserta`}
                  size="small"
                  sx={{
                    ml: 1,
                    bgcolor: alpha("#2e7d32", 0.1),
                    color: "#2e7d32",
                    fontWeight: 600,
                  }}
                />
              </Typography>

              <TextField
                variant="outlined"
                size="small"
                placeholder="Cari peserta..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <Search
                      fontSize="small"
                      sx={{ color: "text.secondary", mr: 1 }}
                    />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        setPage(1);
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{
                  width: isMobile ? "100%" : 300,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "background.paper",
                    "&:hover fieldset": {
                      borderColor: "#2e7d32",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2e7d32",
                    },
                  },
                }}
              />
            </Stack>

            {isMobile ? (
              /* Mobile List View */
              <Box>
                {paginatedAttendances.length > 0 ? (
                  paginatedAttendances.map((attendance, index) => (
                    <Grow in key={attendance.id} timeout={300 + index * 50}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                          borderRadius: 3,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateX(4px)",
                            borderColor: "#2e7d32",
                          },
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              #{index + 1 + (page - 1) * itemsPerPage}
                            </Typography>
                            <Chip
                              label={attendance.position}
                              size="small"
                              sx={{
                                bgcolor: alpha("#2e7d32", 0.08),
                                color: "#2e7d32",
                                fontWeight: 500,
                                fontSize: "0.65rem",
                                height: 20,
                              }}
                            />
                          </Stack>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {attendance.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {attendance.email || "-"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hadir:{" "}
                            {format(new Date(attendance.attendedAt), "PPpp", {
                              locale: id,
                            })}
                          </Typography>
                          {attendance.signature && (
                            <Box sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tanda Tangan:
                              </Typography>
                              <img
                                src={attendance.signature}
                                alt="Tanda tangan"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "50px",
                                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                  borderRadius: "8px",
                                  marginTop: "4px",
                                }}
                              />
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </Grow>
                  ))
                ) : (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      border: `2px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderRadius: 3,
                    }}
                  >
                    {searchTerm ? (
                      <>
                        <Search
                          sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                        />
                        <Typography color="text.secondary">
                          Tidak ditemukan peserta yang sesuai
                        </Typography>
                      </>
                    ) : (
                      <>
                        <PeopleAlt
                          sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                        />
                        <Typography color="text.secondary">
                          Belum ada data kehadiran
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              /* Desktop Table View */
              <TableContainer
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  overflow: "auto",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        align="center"
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        No
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        Nama
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        Jabatan
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        No Telp
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        Waktu Hadir
                      </TableCell>
                      <TableCell
                        sx={{
                          bgcolor: "#2e7d32",
                          color: "white",
                          fontWeight: 700,
                          py: 1.5,
                        }}
                      >
                        Tanda Tangan
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAttendances.map((attendance, index) => (
                      <TableRow
                        key={attendance.id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: alpha("#2e7d32", 0.04),
                          },
                        }}
                      >
                        <TableCell align="center">
                          {index + 1 + (page - 1) * itemsPerPage}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {attendance.name}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={attendance.position}
                            size="small"
                            sx={{
                              bgcolor: alpha("#2e7d32", 0.08),
                              color: "#2e7d32",
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>{attendance.email || "-"}</TableCell>
                        <TableCell>
                          {format(new Date(attendance.attendedAt), "PPpp", {
                            locale: id,
                          })}
                        </TableCell>
                        <TableCell>
                          {attendance.signature ? (
                            <img
                              src={attendance.signature}
                              alt="Tanda tangan"
                              style={{
                                maxWidth: "100px",
                                maxHeight: "40px",
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: "4px",
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Stack
                direction={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Menampilkan {(page - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(page * itemsPerPage, filteredAttendances.length)}{" "}
                  dari {filteredAttendances.length} peserta
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      "&.Mui-selected": {
                        bgcolor: "#2e7d32",
                        color: "white",
                        "&:hover": {
                          bgcolor: "#1b5e20",
                        },
                      },
                    },
                  }}
                />
              </Stack>
            )}
          </Paper>
        </Box>

        {/* Notulen Tab */}
        <Box sx={{ display: tabValue === 1 ? "block" : "none" }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Typography variant="h6" fontWeight={700}>
                📝 Notulen Rapat
              </Typography>
              <Stack direction="row" spacing={1}>
                {!isEditingNotes ? (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setIsEditingNotes(true)}
                    sx={{ borderRadius: 3 }}
                  >
                    Edit Notulen
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Clear />}
                      onClick={() => {
                        setIsEditingNotes(false);
                        setMinutes(meeting?.notes || "");
                      }}
                      sx={{ borderRadius: 3 }}
                    >
                      Batal
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSaveMinutes}
                      disabled={isSaving}
                      sx={{
                        borderRadius: 3,
                        bgcolor: "#2e7d32",
                        "&:hover": { bgcolor: "#1b5e20" },
                      }}
                    >
                      {isSaving ? "Menyimpan..." : "Simpan"}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>

            {isEditingNotes ? (
              <TextField
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                placeholder="Tulis notulen rapat di sini..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                  },
                }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  minHeight: 200,
                  bgcolor: alpha(theme.palette.background.default, 0.3),
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {meeting?.notes || "Belum ada notulen rapat."}
                </Typography>
              </Paper>
            )}
          </Paper>
        </Box>

        {/* QR Code Tab */}
        <Box sx={{ display: tabValue === 2 ? "block" : "none" }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: 4,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
              📱 Tautan Pendaftaran Kehadiran
            </Typography>

            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={4}
              alignItems="center"
            >
              <Box sx={{ flex: 1, textAlign: "center" }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    display: "inline-block",
                    borderRadius: 4,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: alpha(theme.palette.background.default, 0.3),
                    position: "relative",
                  }}
                >
                  <QRCodeSVG
                    id="qr-code"
                    value={meetingLink}
                    size={isMobile ? 180 : 220}
                    level="H"
                    bgColor="#ffffff"
                    fgColor="#1b5e20"
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "background.paper",
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#2e7d32"
                    >
                      SCAN ME
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ flex: 1, width: "100%" }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Tautan Pendaftaran
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: alpha(theme.palette.background.default, 0.3),
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {meetingLink}
                      </Typography>
                      <Tooltip title="Salin Tautan" arrow>
                        <IconButton
                          size="small"
                          onClick={copyToClipboard}
                          sx={{
                            color: "#2e7d32",
                            "&:hover": { bgcolor: alpha("#2e7d32", 0.1) },
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </Box>

                  <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={downloadQRCode}
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        bgcolor: "#2e7d32",
                        py: 1.5,
                        "&:hover": { bgcolor: "#1b5e20" },
                      }}
                    >
                      Download QR Code
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      href={meetingLink}
                      target="_blank"
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        borderColor: "#2e7d32",
                        color: "#2e7d32",
                        "&:hover": {
                          borderColor: "#1b5e20",
                          bgcolor: alpha("#2e7d32", 0.05),
                        },
                      }}
                    >
                      Buka Tautan
                    </Button>
                  </Stack>

                  <Alert
                    severity="info"
                    icon={<QrCode />}
                    sx={{
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                    }}
                  >
                    Scan QR Code atau buka tautan untuk melakukan pendaftaran
                    kehadiran.
                  </Alert>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="success"
            sx={{
              borderRadius: 3,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
}
